import {
  pgTable,
  text,
  varchar,
  uuid,
  timestamp,
  integer,
  date,
  pgEnum,
  numeric,
  jsonb,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// Cross-domain imports
import { deals } from "./schema-deals.js";
import { contacts } from "./schema-crm.js";

// Helper: Drizzle numeric() maps to string in TS. Accept both string and number from API,
// output string for Drizzle. Preserves null/undefined.
const zNumericString = z.preprocess(
  (v) => (v != null ? String(v) : v),
  z.string().optional().nullable(),
);

// ============================================================================
// CREDIT APPLICATIONS / LENDER SUBMISSIONS MODULE - SPRINT-19
// Credit applications, lender submissions, conditions, and communications
// PRD Reference: docs/prds/PRD-CREDIT-APPLICATIONS.md
// ============================================================================

// ============================================================================
// ENUMS
// NOTE: All enums already exist in the baseline schema (DO NOT recreate in SQL).
// Defined here purely for Drizzle type-safety.
// ============================================================================

export const applicationStatusEnum = pgEnum("application_status", [
  "draft",
  "pending_review",
  "in_review",
  "approved",
  "conditionally_approved",
  "declined",
  "withdrawn",
  "expired",
]);

export const applicationTypeEnum = pgEnum("application_type", [
  "new",
  "variation",
  "refinance",
  "top_up",
  "restructure",
]);

export const submissionTypeEnum = pgEnum("submission_type", [
  "initial",
  "resubmission",
  "variation",
  "appeal",
]);

export const submissionMethodEnum = pgEnum("submission_method", [
  "portal",
  "email",
  "broker_platform",
  "api",
]);

export const communicationTypeEnum = pgEnum("communication_type", [
  "submission",
  "query",
  "response",
  "update",
  "approval",
  "decline",
  "condition_request",
]);

export const communicationDirectionEnum = pgEnum("communication_direction", [
  "inbound",
  "outbound",
]);

export const conditionTypeEnum = pgEnum("condition_type", [
  "precedent",
  "subsequent",
  "ongoing",
]);

export const conditionCategoryEnum = pgEnum("condition_category", [
  "documentation",
  "valuation",
  "insurance",
  "legal",
  "financial",
  "security",
  "other",
]);

export const conditionStatusEnum = pgEnum("condition_status", [
  "outstanding",
  "submitted",
  "under_review",
  "satisfied",
  "waived",
  "not_applicable",
]);

export const documentVerificationStatusEnum = pgEnum("document_verification_status", [
  "pending",
  "verified",
  "rejected",
  "expired",
  "not_required",
]);

// ============================================================================
// TABLES
// ============================================================================

// ── Credit Applications ──────────────────────────────────────────────────────

export const creditApplications = pgTable("crm_x_credit_applications", {
  id: uuid("id").primaryKey().defaultRandom(),
  dealId: uuid("deal_id").notNull().references(() => deals.id, { onDelete: "cascade" }),
  applicationReference: varchar("application_reference", { length: 50 }),
  lenderReference: varchar("lender_reference", { length: 100 }),
  lenderId: uuid("lender_id"),
  lenderProductId: uuid("lender_product_id"),
  bdmContactId: uuid("bdm_contact_id").references(() => contacts.id, { onDelete: "set null" }),
  applicationType: applicationTypeEnum("application_type").notNull().default("new"),
  applicationStatus: applicationStatusEnum("application_status").notNull().default("draft"),
  requestedAmount: numeric("requested_amount", { precision: 15, scale: 2 }),
  approvedAmount: numeric("approved_amount", { precision: 15, scale: 2 }),
  facilityLimit: numeric("facility_limit", { precision: 15, scale: 2 }),
  proposedRate: numeric("proposed_rate", { precision: 5, scale: 4 }),
  approvedRate: numeric("approved_rate", { precision: 5, scale: 4 }),
  assessmentRate: numeric("assessment_rate", { precision: 5, scale: 4 }),
  createdDate: date("created_date").notNull().default("2026-01-01" as unknown as string),
  submittedDate: date("submitted_date"),
  decisionDate: date("decision_date"),
  approvalExpiryDate: date("approval_expiry_date"),
  expectedSettlementDate: date("expected_settlement_date"),
  actualSettlementDate: date("actual_settlement_date"),
  checklistId: uuid("checklist_id"),
  documentsComplete: integer("documents_complete").default(0),
  documentsVerified: integer("documents_verified").default(0),
  servicingAtActualRate: numeric("servicing_at_actual_rate", { precision: 5, scale: 2 }),
  servicingAtAssessmentRate: numeric("servicing_at_assessment_rate", { precision: 5, scale: 2 }),
  lvrTotal: numeric("lvr_total", { precision: 5, scale: 2 }),
  sharepointFolderUrl: text("sharepoint_folder_url"),
  sharepointFolderId: varchar("sharepoint_folder_id", { length: 100 }),
  declineReason: text("decline_reason"),
  withdrawalReason: text("withdrawal_reason"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: false }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: false }).notNull().defaultNow(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// ── Application Documents ────────────────────────────────────────────────────

export const applicationDocuments = pgTable("crm_x_application_documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  applicationId: uuid("application_id").notNull().references(() => creditApplications.id, { onDelete: "cascade" }),
  entityType: varchar("entity_type", { length: 20 }),
  entityId: uuid("entity_id"),
  documentName: varchar("document_name", { length: 500 }).notNull(),
  documentTypeId: uuid("document_type_id"),
  documentTypeManual: varchar("document_type_manual", { length: 100 }),
  documentCategory: varchar("document_category", { length: 100 }),
  checklistItemId: uuid("checklist_item_id"),
  originalFilename: varchar("original_filename", { length: 500 }),
  fileExtension: varchar("file_extension", { length: 10 }),
  fileSizeBytes: integer("file_size_bytes"),
  mimeType: varchar("mime_type", { length: 100 }),
  pageCount: integer("page_count"),
  storageType: varchar("storage_type", { length: 50 }).default("sharepoint"),
  sharepointItemId: varchar("sharepoint_item_id", { length: 100 }),
  sharepointDriveId: varchar("sharepoint_drive_id", { length: 100 }),
  sharepointUrl: text("sharepoint_url"),
  supabaseStoragePath: text("supabase_storage_path"),
  aiClassifiedType: varchar("ai_classified_type", { length: 100 }),
  aiConfidenceScore: numeric("ai_confidence_score", { precision: 5, scale: 4 }),
  aiExtractedData: jsonb("ai_extracted_data"),
  aiProcessingStatus: varchar("ai_processing_status", { length: 20 }).default("pending"),
  verificationStatus: documentVerificationStatusEnum("verification_status").default("pending"),
  verifiedBy: uuid("verified_by"),
  verifiedAt: timestamp("verified_at", { withTimezone: false }),
  verificationNotes: text("verification_notes"),
  documentDate: date("document_date"),
  documentPeriodStart: date("document_period_start"),
  documentPeriodEnd: date("document_period_end"),
  expiryDate: date("expiry_date"),
  versionNumber: integer("version_number").default(1),
  previousVersionId: uuid("previous_version_id"),
  isCurrent: integer("is_current").default(1),
  createdAt: timestamp("created_at", { withTimezone: false }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: false }).notNull().defaultNow(),
  uploadedBy: uuid("uploaded_by"),
});

// ── Lender Submissions ───────────────────────────────────────────────────────

export const lenderSubmissions = pgTable("crm_x_lender_submissions", {
  id: uuid("id").primaryKey().defaultRandom(),
  applicationId: uuid("application_id").notNull().references(() => creditApplications.id, { onDelete: "cascade" }),
  submissionReference: varchar("submission_reference", { length: 50 }),
  submissionType: submissionTypeEnum("submission_type").notNull().default("initial"),
  submissionMethod: submissionMethodEnum("submission_method").notNull().default("portal"),
  submissionDate: timestamp("submission_date", { withTimezone: false }),
  bdmContactId: uuid("bdm_contact_id").references(() => contacts.id, { onDelete: "set null" }),
  creditAnalystName: varchar("credit_analyst_name", { length: 255 }),
  creditAnalystEmail: varchar("credit_analyst_email", { length: 255 }),
  coverLetter: text("cover_letter"),
  executiveSummary: text("executive_summary"),
  submissionNotes: text("submission_notes"),
  documentCount: integer("document_count").default(0),
  documentsIncluded: jsonb("documents_included"),
  portalSubmissionId: varchar("portal_submission_id", { length: 100 }),
  portalStatus: varchar("portal_status", { length: 50 }),
  responseReceived: integer("response_received").default(0),
  responseDate: timestamp("response_date", { withTimezone: false }),
  responseType: varchar("response_type", { length: 50 }),
  responseSummary: text("response_summary"),
  conditionsReceived: integer("conditions_received").default(0),
  createdAt: timestamp("created_at", { withTimezone: false }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: false }).notNull().defaultNow(),
  submittedBy: uuid("submitted_by"),
});

// ── Submission Communications ────────────────────────────────────────────────

export const submissionCommunications = pgTable("crm_x_submission_communications", {
  id: uuid("id").primaryKey().defaultRandom(),
  applicationId: uuid("application_id").notNull().references(() => creditApplications.id, { onDelete: "cascade" }),
  submissionId: uuid("submission_id").references(() => lenderSubmissions.id, { onDelete: "set null" }),
  // condition_id FK — self-referential avoidance: defined as plain uuid to prevent circular deps
  conditionId: uuid("condition_id"),
  communicationType: communicationTypeEnum("communication_type").notNull().default("submission"),
  direction: communicationDirectionEnum("direction").notNull().default("outbound"),
  communicationDate: timestamp("communication_date", { withTimezone: false }).notNull(),
  subject: varchar("subject", { length: 500 }),
  content: text("content"),
  summary: text("summary"),
  fromName: varchar("from_name", { length: 255 }),
  fromEmail: varchar("from_email", { length: 255 }),
  toNames: text("to_names"),
  toEmails: text("to_emails"),
  ccEmails: text("cc_emails"),
  hasAttachments: integer("has_attachments").default(0),
  attachmentCount: integer("attachment_count").default(0),
  attachments: jsonb("attachments"),
  outlookMessageId: varchar("outlook_message_id", { length: 200 }),
  outlookConversationId: varchar("outlook_conversation_id", { length: 200 }),
  outlookWebLink: text("outlook_web_link"),
  requiresResponse: integer("requires_response").default(0),
  responseDueDate: date("response_due_date"),
  responseReceived: integer("response_received").default(0),
  responseCommunicationId: uuid("response_communication_id"),
  createdAt: timestamp("created_at", { withTimezone: false }).notNull().defaultNow(),
  createdBy: uuid("created_by"),
});

// ── Conditions ───────────────────────────────────────────────────────────────

export const conditions = pgTable("crm_x_conditions", {
  id: uuid("id").primaryKey().defaultRandom(),
  applicationId: uuid("application_id").notNull().references(() => creditApplications.id, { onDelete: "cascade" }),
  submissionId: uuid("submission_id").references(() => lenderSubmissions.id, { onDelete: "set null" }),
  conditionType: conditionTypeEnum("condition_type").notNull().default("precedent"),
  conditionCategory: conditionCategoryEnum("condition_category").notNull().default("documentation"),
  conditionNumber: integer("condition_number"),
  conditionDescription: text("condition_description").notNull(),
  conditionShortName: varchar("condition_short_name", { length: 255 }),
  responsibleParty: varchar("responsible_party", { length: 100 }),
  requiredDocumentTypeId: uuid("required_document_type_id"),
  requiredDocumentDescription: varchar("required_document_description", { length: 255 }),
  requiredAction: text("required_action"),
  conditionStatus: conditionStatusEnum("condition_status").default("outstanding"),
  priority: integer("priority").default(3),
  receivedDate: date("received_date"),
  dueDate: date("due_date"),
  submittedDate: date("submitted_date"),
  satisfiedDate: date("satisfied_date"),
  brokerNotes: text("broker_notes"),
  lenderFeedback: text("lender_feedback"),
  createdAt: timestamp("created_at", { withTimezone: false }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: false }).notNull().defaultNow(),
  createdBy: uuid("created_by"),
  satisfiedBy: uuid("satisfied_by"),
});

// ── Condition Documents ──────────────────────────────────────────────────────

export const conditionDocuments = pgTable("crm_x_condition_documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  conditionId: uuid("condition_id").notNull().references(() => conditions.id, { onDelete: "cascade" }),
  documentId: uuid("document_id").notNull().references(() => applicationDocuments.id, { onDelete: "cascade" }),
  submissionId: uuid("submission_id").references(() => lenderSubmissions.id, { onDelete: "set null" }),
  lenderAccepted: integer("lender_accepted").default(0),
  lenderNotes: text("lender_notes"),
  createdAt: timestamp("created_at", { withTimezone: false }).notNull().defaultNow(),
  linkedBy: uuid("linked_by"),
});

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

// ── Credit Applications ──────────────────────────────────────────────────────

export const insertCreditApplicationSchema = createInsertSchema(creditApplications, {
  dealId: z.string().uuid(),
  lenderId: z.string().uuid().optional().nullable(),
  lenderProductId: z.string().uuid().optional().nullable(),
  bdmContactId: z.string().uuid().optional().nullable(),
  checklistId: z.string().uuid().optional().nullable(),
  createdBy: z.string().uuid().optional().nullable(),
  updatedBy: z.string().uuid().optional().nullable(),
  // Numeric fields: accept numbers from API, coerce to string for Drizzle numeric()
  requestedAmount: zNumericString,
  approvedAmount: zNumericString,
  facilityLimit: zNumericString,
  proposedRate: zNumericString,
  approvedRate: zNumericString,
  assessmentRate: zNumericString,
  servicingAtActualRate: zNumericString,
  servicingAtAssessmentRate: zNumericString,
  lvrTotal: zNumericString,
});

export const selectCreditApplicationSchema = createSelectSchema(creditApplications);

export const updateCreditApplicationSchema = insertCreditApplicationSchema.partial();

// ── Application Documents ────────────────────────────────────────────────────

export const insertApplicationDocumentSchema = createInsertSchema(applicationDocuments, {
  applicationId: z.string().uuid(),
  documentTypeId: z.string().uuid().optional().nullable(),
  checklistItemId: z.string().uuid().optional().nullable(),
  entityId: z.string().uuid().optional().nullable(),
  verifiedBy: z.string().uuid().optional().nullable(),
  previousVersionId: z.string().uuid().optional().nullable(),
  uploadedBy: z.string().uuid().optional().nullable(),
  aiConfidenceScore: zNumericString,
  // Timestamp overrides: JSON serialisation sends ISO strings, not Date objects
  verifiedAt: z.coerce.date().optional().nullable(),
});

export const selectApplicationDocumentSchema = createSelectSchema(applicationDocuments);

export const updateApplicationDocumentSchema = insertApplicationDocumentSchema.partial();

// ── Lender Submissions ───────────────────────────────────────────────────────

export const insertLenderSubmissionSchema = createInsertSchema(lenderSubmissions, {
  applicationId: z.string().uuid(),
  bdmContactId: z.string().uuid().optional().nullable(),
  submittedBy: z.string().uuid().optional().nullable(),
  // Timestamp overrides
  submissionDate: z.coerce.date().optional().nullable(),
  responseDate: z.coerce.date().optional().nullable(),
});

export const selectLenderSubmissionSchema = createSelectSchema(lenderSubmissions);

export const updateLenderSubmissionSchema = insertLenderSubmissionSchema.partial();

// ── Submission Communications ────────────────────────────────────────────────

export const insertSubmissionCommunicationSchema = createInsertSchema(submissionCommunications, {
  applicationId: z.string().uuid(),
  submissionId: z.string().uuid().optional().nullable(),
  conditionId: z.string().uuid().optional().nullable(),
  responseCommunicationId: z.string().uuid().optional().nullable(),
  createdBy: z.string().uuid().optional().nullable(),
  // Timestamp overrides
  communicationDate: z.coerce.date(),
});

export const selectSubmissionCommunicationSchema = createSelectSchema(submissionCommunications);

export const updateSubmissionCommunicationSchema = insertSubmissionCommunicationSchema.partial();

// ── Conditions ───────────────────────────────────────────────────────────────

export const insertConditionSchema = createInsertSchema(conditions, {
  applicationId: z.string().uuid(),
  submissionId: z.string().uuid().optional().nullable(),
  requiredDocumentTypeId: z.string().uuid().optional().nullable(),
  createdBy: z.string().uuid().optional().nullable(),
  satisfiedBy: z.string().uuid().optional().nullable(),
});

export const selectConditionSchema = createSelectSchema(conditions);

export const updateConditionSchema = insertConditionSchema.partial();

// ── Condition Documents ──────────────────────────────────────────────────────

export const insertConditionDocumentSchema = createInsertSchema(conditionDocuments, {
  conditionId: z.string().uuid(),
  documentId: z.string().uuid(),
  submissionId: z.string().uuid().optional().nullable(),
  linkedBy: z.string().uuid().optional().nullable(),
});

export const selectConditionDocumentSchema = createSelectSchema(conditionDocuments);

export const updateConditionDocumentSchema = insertConditionDocumentSchema.partial();

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type CreditApplication = typeof creditApplications.$inferSelect;
export type InsertCreditApplication = typeof creditApplications.$inferInsert;

export type ApplicationDocument = typeof applicationDocuments.$inferSelect;
export type InsertApplicationDocument = typeof applicationDocuments.$inferInsert;

export type LenderSubmission = typeof lenderSubmissions.$inferSelect;
export type InsertLenderSubmission = typeof lenderSubmissions.$inferInsert;

export type SubmissionCommunication = typeof submissionCommunications.$inferSelect;
export type InsertSubmissionCommunication = typeof submissionCommunications.$inferInsert;

export type Condition = typeof conditions.$inferSelect;
export type InsertCondition = typeof conditions.$inferInsert;

export type ConditionDocument = typeof conditionDocuments.$inferSelect;
export type InsertConditionDocument = typeof conditionDocuments.$inferInsert;
